import React, {
  RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import {
  VisitNoteServiceCode,
} from 'views/EMR/components/ServiceLookup/type';
import Button from 'pages/Dashboard/components/Button';
import {
  focusInput,
  isEmptyString,
} from '@populateui/utils';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import clsx from 'clsx';
import EditIcon from 'core/icons/edit';
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from 'react-hook-form';
import isNil from 'lodash/isNil';
import Popover from 'core/components/Popover';
import ToolTip from 'core/components/Tooltip';
import IconButton from '@mui/material/IconButton';
import {
  serviceLookupEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import TextArea from 'pages/Dashboard/components/Input/Textarea';

type Props = {
  data: VisitNoteServiceCode;
  index?: number;
};

type FormData = {
  lineNote?: string | null;
};

type NotesEditorProps = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  index: number;
  data: VisitNoteServiceCode;
};

function NotesForm() {
  const { control } = useFormContext<FormData>();

  const inputRef = useRef<HTMLTextAreaElement>(undefined);

  useEffect(() => {
    focusInput(inputRef?.current);
  }, []);

  return (
    <div className="flex gap-2 populate-form items-start populate-form-sm">
      <Controller
        control={control}
        name="lineNote"
        render={({ field }) => (
          <TextArea
            {...field}
            placeholder="Add Notes (Max 32 characters)"
            rows={2}
            id="service-code-notes"
            trackingLabel="Service Code Notes"
            maxLength={32}
            className="!max-h-[120px] pr-1 w-full"
            value={field.value ?? ''}
            inputRef={inputRef as RefObject<HTMLTextAreaElement>}
          />
        )}
      />
    </div>
  );
}

function NotesEditor({
  anchorEl,
  onClose,
  index,
  data,
}: NotesEditorProps) {
  const { lineNote } = data?.serviceCodeDetails ?? {};

  const formProps = useForm<FormData>({
    defaultValues: {
      lineNote: lineNote ?? '',
    },
  });

  const { reset, handleSubmit } = formProps;

  const handleClose = () => {
    onClose();
    reset({
      lineNote: lineNote ?? '',
    });
  };

  const onSubmit = ({ lineNote }: FormData) => {
    serviceLookupEventChannel.emit('onServiceCodeLineUpdate', {
      index: index ?? 0,
      value: lineNote ?? '',
      id: 'lineNote',
      data,
    });
    onClose();
  };

  return (
    <Popover
      anchorEl={anchorEl}
      onClose={handleClose}
      classes={{ paper: '!w-[420px] ' }}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <FormProvider {...formProps}>
        <div className="p-4 flex flex-col gap-2">
          <NotesForm />
          <div className="flex justify-between items-center gap-2">
            <Button
              variant="outlined"
              type="button"
              className="flex-1 !max-w-[unset]"
              onClick={() => handleClose()}
            >
              Close
            </Button>
            <Button
              variant="contained"
              className="flex-1 !max-w-[unset]"
              color="primary"
              type="submit"
              onClick={handleSubmit(onSubmit)}
            >
              Confirm
            </Button>
          </div>
        </div>
      </FormProvider>
    </Popover>
  );
}

function NotesCell({
  data,
  index,
}: Props) {
  const {
    lineNote,
  } = data?.serviceCodeDetails ?? {};

  const empty = isEmptyString(lineNote);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const notesRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  return (
    <div className="flex items-center gap-1 w-full text-xs">
      <Button
        variant="outlined"
        color="primary"
        className={clsx('flex !px-1', {
          '!font-medium !border-dashed !py-0 !rounded-full !w-5 !h-5 !min-w-[initial] !flex !items-center !border-blue-500': empty,
          '!text-black !min-w-[120px] !border-0 gap-1 !justify-between !py-0.5 !w-[200px] !max-w-[initial]': !empty,
        })}
        onClick={(e) => {
          e.stopPropagation();
          buttonRef?.current?.focus();
          setAnchorEl(e.currentTarget?.parentElement);
        }}
        ref={buttonRef}
      >
        {empty ? (
          <AddIcon className="!w-4 !h-4 !text-inherit" />
        ) : (
          <>
            <div className="flex items-start text-[11px] truncate gap-1 text-left font-medium w-fit max-w-full">
              <ToolTip title={lineNote} auto ref={notesRef}>
                <span className="truncate" ref={notesRef}>{lineNote}</span>
              </ToolTip>
            </div>
            {!empty && (<EditIcon className="!w-4 !h-4 !text-gray-800" />)}
          </>
        )}
      </Button>
      {!empty && (
        <ToolTip title="Remove Notes">
          <IconButton
            className="!p-0"
            onClick={() => {
              serviceLookupEventChannel.emit('onServiceCodeLineUpdate', {
                index: index ?? 0,
                value: '',
                id: 'lineNote',
                data,
              });
            }}
          >
            <CloseIcon className="!w-4 !h-4" />
          </IconButton>
        </ToolTip>
      )}
      {!isNil(anchorEl) && (
        <NotesEditor
          anchorEl={anchorEl}
          onClose={handleClose}
          index={index ?? 0}
          data={data}
        />
      )}
    </div>
  );
}

export default {
  id: 'notes',
  columnTitle: 'Notes',
  multiLineHeader: true,
  size: 180,
  minSize: 150,
  cell: ({ row }) => <NotesCell data={row.original} index={row.index} />,
} as ColumnDef<VisitNoteServiceCode>;
